'use server';

/**
 * Story-subject read seam (A2). The content-creation wizard picks the "child this
 * story is about/for" from the family's people, sourced from the CENTRAL accounts
 * people API (members + managed children).
 *
 * SCOPE: this is a dedicated seam for the STORY-SUBJECT picker only — the `id`
 * returned is the CENTRAL person id (per_…); bridge to a portal member id with
 * `portalMemberIdForCentralPerson` when local story data must be reconciled.
 */

import { SessionService } from '@/contexts/authentication';
import { getCentralPeopleClient } from '../central/CentralPeopleClient';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';
import type { ActionResult } from '@/shared/types/actions';

/** The subset of a person the story-subject picker consumes (source-agnostic). */
export interface StorySubject {
  id: string;
  name: string;
  birthDate?: string;
  isChild: boolean;
}

/**
 * The family's people as story-subject candidates — central people (members +
 * managed children). Includes adults + children (the caller filters by `isChild`
 * for the child picker).
 */
export async function getStorySubjects(): Promise<StorySubject[] | null> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return null;

  const people = await getCentralPeopleClient().getPeople(familyId);
  return people.map((p) => ({
    id: p.id,
    name: p.name,
    birthDate: p.birthDate,
    isChild: p.isChild,
  }));
}

/**
 * Add a child subject → central addPerson (a MANAGED person, no login).
 */
export async function addStorySubject(data: {
  name: string;
  birthDate?: string;
}): Promise<ActionResult<StorySubject>> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };
    const person = await getCentralPeopleClient().addPerson(familyId, {
      name: data.name,
      birthDate: data.birthDate,
      relation: 'child',
    });
    return {
      success: true,
      data: { id: person.id, name: person.name, birthDate: person.birthDate, isChild: true },
    };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('storySubjects', e) };
  }
}
